import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';
import type { UpdateEndPage } from '@/app/survey/_hooks/useUpdateEndPageMutation';
import type { UpdateIntroPage } from '@/app/survey/_hooks/useUpdateIntroPageMutation';
import type { CreateOrUpdateSurveyFormValues } from '@/components/SurveySettingsModal/SurveySettingsModal';
import type { Question, QuestionDataJson } from '@/models/question';
import type { SurveyBase } from '@/models/survey';
import type { SurveyType } from '@/models/surveyType';
import services from '@/services';

dayjs.extend(utc);

export const formatSurveyDataToFormData = ({
   values,
   statusId,
   userId,
   surveyType,
}: {
   values: CreateOrUpdateSurveyFormValues;
   statusId?: string;
   userId?: string;
   surveyType?: Partial<SurveyType>;
}): Record<string, FormDataEntryValue | string> => {
   const formData = new FormData();

   formData.set('is_shufflable', values.isShuffle ? '1' : '0');

   if (values.selectedEvent) {
      formData.set('is_authenticable', '1');
   } else {
      formData.set('is_authenticable', values.requireAuth ? '1' : '0');
   }

   if (surveyType?.id) {
      formData.set('survey_type_id', surveyType.id);
   }

   if (userId) {
      formData.set('creator_id', userId);
   }

   if (statusId) {
      formData.set('survey_status_id', statusId);
   }

   if (values.title) {
      formData.set(`title`, values.title);
   }

   if (surveyType?.slug === 'graded') {
      formData.set(`has_results_showed`, values.displayGrade ? '1' : '0');
   }

   if (surveyType?.slug === 'graded') {
      formData.set('timer', values.timer ?? '');
   }

   if (surveyType?.slug === 'graded') {
      const number =
         typeof values.totalGrade === 'number'
            ? values.totalGrade
            : Number(values.totalGrade);

      if (Number.isFinite(number) && number > 0) {
         formData.set('total_grade', String(number));
      }
   }

   if (values.title_alignment) {
      formData.set(`title_alignment`, values.title_alignment);
   }

   if (values.endDate) {
      formData.set(
         'expires_at',
         dayjs(values.endDate).format('YYYY-MM-DD HH:mm'),
      );
   } else {
      formData.set('expires_at', '');
   }

   if (Object.hasOwn(values, 'maxSend')) {
      if (values.maxSend) {
         formData.set(`max_entries`, values.maxSend.toString());
      }
   }

   if (values.endDateMessage) {
      formData.set('end_availability_message', values.endDateMessage);
   }

   const data: { [key: string]: FormDataEntryValue | File | string } = {};

   formData.forEach((value, key) => (data[key] = value));
   const jsonData = JSON.stringify(data);

   return JSON.parse(jsonData);
};

export const formatIntroDatatoFormData = (
   values: UpdateIntroPage,
   initValue?: SurveyBase,
   surveyId?: string,
) => {
   const formData = new FormData();

   formData.append('mutate[0][operation]', 'update');
   formData.append('mutate[0][key]', surveyId ?? '');

   if (values.description !== undefined) {
      formData.set(
         'mutate[0][attributes][description]',
         values.description ?? '',
      );
   }

   let count = 0;

   if (typeof values.footerImage === 'object') {
      formData.set(`mutate[0][media][${count}][file]`, values.footerImage);
      formData.set(`mutate[0][media][${count}][collection_name]`, 'footer');
      count++;
   }

   if (
      typeof values.footerImage === 'undefined' &&
      initValue?.media.filter((item) => item.collection_name === 'footer')
         .length !== 0
   ) {
      const footerImage = initValue?.media.filter(
         (item) => item.collection_name === 'footer',
      );

      if (footerImage) {
         services.client.deleteMedia(footerImage[0].id);
      }
      count++;
   }

   if (typeof values.headerImage === 'object') {
      formData.set(`mutate[0][media][${count}][file]`, values.headerImage);
      formData.set(`mutate[0][media][${count}][collection_name]`, 'logo');
      count++;
   }

   if (
      typeof values.headerImage === 'undefined' &&
      initValue?.media.filter((item) => item.collection_name === 'logo')
         .length !== 0
   ) {
      const headerImage = initValue?.media.filter(
         (item) => item.collection_name === 'logo',
      );

      if (headerImage) {
         services.client.deleteMedia(headerImage[0].id);
      }
      count++;
   }
   return formData;
};

export const formatEndPageDatatoFormData = ({
   values,
}: {
   values: UpdateEndPage;
}): Record<string, FormDataEntryValue | string> => {
   const formData = new FormData();

   if (values.endPageDescription !== undefined) {
      formData.set(`end_page_message`, values.endPageDescription ?? '');
   }

   const data: { [key: string]: FormDataEntryValue | File | string } = {};

   formData.forEach((value, key) => (data[key] = value));
   const jsonData = JSON.stringify(data);

   return JSON.parse(jsonData);
};

export const formatEmptyQuestionToJson = (
   pageId: string,
   type: string,
   order?: number,
): QuestionDataJson => {
   const result: QuestionDataJson = {
      question_type_id: type,
      questionable_type: 'App\\Models\\Page',
      questionable_id: pageId,
      label: t('question.new'),
      is_required: false,
      is_visible: true,
   };

   if (order !== undefined) {
      result.order = order;
   }

   return result;
};

export const mapQuestionToAnswers = (question: Question) => {
   switch (question.question_type.slug) {
      case 'file':
         if (question.answer) {
            return [
               {
                  id: question.id,
                  type: `App\\Models\\Question`,
                  is_escape_answer: 0,
                  file: question.answer.file,
               },
            ];
         }
         return [];
      case 'checkbox': {
         const checkbox = [];

         checkbox.push(
            ...question.linked_choices
               .filter((choice) => choice.is_selected)
               .map((choice) => ({
                  id: choice.id,
                  type: `App\\Models\\QuestionChoice`,
               })),
         );

         return checkbox;
      }
      case 'select':
      case 'radio':
         if (question.select_value) {
            return [
               {
                  id: question.select_value,
                  type: `App\\Models\\QuestionChoice`,
               },
            ];
         }
         return [];
      case 'datetime':
         if (question.escape_value || question.answer) {
            let formattedValue;
            const questionOption = question.personalization?.option;

            switch (questionOption) {
               case 'datetime':
                  formattedValue =
                     dayjs(question.answer).format('YYYY-MM-DDTHH:mm:ss') +
                     '.000Z';
                  break;
               case 'date':
                  formattedValue = dayjs(question.answer).format('YYYY-MM-DD');
                  break;
               case 'time':
                  formattedValue = `${question.answer}:00`;
                  break;
               default:
                  formattedValue = question.escape_value;
            }

            return [
               {
                  id: question.id,
                  type: `App\\Models\\Question`,
                  is_escape_answer: question.answer ? 0 : 1,
                  value: formattedValue,
               },
            ];
         }
         return [];
      case 'rating-star':
         if (question.answer && question.answer !== '0') {
            return [
               {
                  id: question.linked_choices[question.answer - 1].id,
                  type: `App\\Models\\QuestionChoice`,
                  is_escape_answer: 0,
               },
            ];
         } else if (question.answer && question.answer === '0') {
            return [
               {
                  id: question.linked_choices[0].id,
                  type: `App\\Models\\QuestionChoice`,
                  is_escape_answer: 0,
               },
            ];
         }
      default:
         if (!question.answer) return [];
         return [
            {
               id: question.id,
               type: `App\\Models\\Question`,
               is_escape_answer: 0,
               value: question.answer,
            },
         ];
   }
};
